// SEO META — sumber kebenaran meta tags semua halaman (SPA). Halaman cukup
// panggil setPageMeta(title, desc, opts). JANGAN edit meta satu-satu manual.
import { getOptimizedImageUrl } from './imageUtils';

const updateMeta = (name: string, content: string) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.setAttribute('content', content);
};

const updateOg = (property: string, content: string) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (el) el.setAttribute('content', content);
};

// Twitter resmi pakai `name=`; fallback `property=` untuk meta lama.
const updateTwitter = (name: string, content: string) => {
  let el = document.querySelector(`meta[name="twitter:${name}"]`);
  if (!el) el = document.querySelector(`meta[property="twitter:${name}"]`);
  if (el) el.setAttribute('content', content);
};

const updateCanonical = (url: string) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (el) el.setAttribute('href', url);
};

// ROBOTS otomatis per path (admin → noindex). Tambah rute publik baru di sini.
// AUTH (/login /daftar /lupa-sandi /reset-password) SENGAJA noindex (2026-10-26):
// halaman login terindeks = duplikat + phishing surface, tanpa nilai SEO.
const PUBLIC_EXACT_PATHS = ['/', '/privacy', '/terms', '/cek-resi', '/cek-ongkir'];
const PUBLIC_PREFIXES = ['/f/', '/lp/', '/ai-lp/', '/d/', '/p/', '/go/payment/'];

export const isIndexablePath = (pathname: string): boolean => {
  if (PUBLIC_EXACT_PATHS.includes(pathname)) return true;
  return PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

const updateRobots = (indexable: boolean) => {
  const value = indexable ? 'index, follow' : 'noindex, nofollow';
  updateMeta('robots', value);
  let googlebot = document.querySelector('meta[name="googlebot"]');
  if (!googlebot) {
    googlebot = document.createElement('meta');
    googlebot.setAttribute('name', 'googlebot');
    document.head.appendChild(googlebot);
  }
  googlebot.setAttribute('content', value);
};

// SET PAGE META — panggil dari hook/useEffect setiap halaman.
export interface PageMetaOptions {
  indexable?: boolean;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
}

export const setPageMeta = (title: string, description?: string, options?: PageMetaOptions) => {
  document.title = title;
  updateMeta('title', title);
  updateOg('og:title', title);
  updateTwitter('title', title);
  const canonicalUrl = window.location.href.split('?')[0].split('#')[0];
  updateCanonical(canonicalUrl);
  updateOg('og:url', canonicalUrl);
  updateTwitter('url', canonicalUrl);
  if (description) {
    updateMeta('description', description);
    updateOg('og:description', description);
    updateTwitter('description', description);
  }
  const indexable = options?.indexable ?? isIndexablePath(window.location.pathname);
  updateRobots(indexable);
  // OG image Supabase otomatis dikompres (w1200/q80/webp) agar crawler tak unduh original.
  if (options?.image) {
    const ogImage = getOptimizedImageUrl(options.image, { width: 1200, quality: 80 }) ?? options.image;
    updateOg('og:image', ogImage);
    updateTwitter('image', ogImage);
    updateOg('og:image:alt', options.imageAlt || title);
    if (options.imageWidth) updateOg('og:image:width', String(options.imageWidth));
    if (options.imageHeight) updateOg('og:image:height', String(options.imageHeight));
    updateTwitter('card', 'summary_large_image');
  }
};

// Halaman lama yang pakai setPageMetaWithImage tetap jalan via sini.
export const setPageMetaWithImage = (title: string, description: string, imageUrl?: string) => {
  setPageMeta(title, description, imageUrl ? { image: imageUrl } : undefined);
};

export const ROOT_TITLE = 'CuanMax - Platform Manajemen Pesanan & Bisnis Digital';
export const ROOT_DESCRIPTION = 'Platform all-in-one untuk mengelola pesanan, pelanggan, komisi, dan penghasilan tim secara real-time. Dilengkapi form builder, analytics, dan tracking pixels.';
